import { useRef } from 'react';
import Joystick, { GhostArea, Direction, DirectionCount } from 'rc-joystick';
import type { IJoystickChangeValue } from 'rc-joystick';
import { useSound, useEffects } from '@/contexts';

interface TouchControlsProps {
  onMove: (direction: 'up' | 'down' | 'left' | 'right') => void;
  disabled?: boolean;
  showVisualFeedback?: boolean;
}

export const TouchControls: React.FC<TouchControlsProps> = ({
  onMove,
  disabled = false,
  showVisualFeedback = true,
}) => {
  const { playMove } = useSound();
  const { triggerEffect } = useEffects();
  const lastDirectionRef = useRef<string | null>(null);
  const isActiveRef = useRef<boolean>(false);

  const handleChange = (data: IJoystickChangeValue) => {
    // Only process non-center directions
    if (data.direction === Direction.Center) {
      return;
    }
    
    let direction: 'up' | 'down' | 'left' | 'right' | null = null;
    
    // Map rc-joystick directions to our game directions
    switch (data.direction) {
      case Direction.Top:
      case Direction.TopLeft:
      case Direction.RightTop:
        direction = 'up';
        break;
      case Direction.Bottom:
      case Direction.BottomRight:
      case Direction.LeftBottom:
        direction = 'down';
        break;
      case Direction.Left:
        direction = 'left';
        break;
      case Direction.Right:
        direction = 'right';
        break;
    }
    
    // Only trigger if direction changed
    if (direction && direction !== lastDirectionRef.current) {
      lastDirectionRef.current = direction;
      
      // Play sound
      playMove();
      
      // Trigger move
      onMove(direction);
      
      // Visual feedback
      if (showVisualFeedback) {
        const positions: Record<string, [number, number]> = {
          up: [50, 30],
          down: [50, 70],
          left: [30, 50],
          right: [70, 50],
        };

        triggerEffect({
          type: 'glow',
          position: positions[direction],
          duration: 200,
          color: '#4ecdc4',
        });
      }
    }
  };

  const handleActiveChange = (active: boolean) => {
    isActiveRef.current = active;
    if (!active) {
      lastDirectionRef.current = null;
    }
  };

  if (disabled) return null;

  return (
    <div
      className="touch-controls-container"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 10,
      }}
    >
      <GhostArea 
        className="touch-controls-ghost"
        width="100%" 
        height="100%"
      >
        <Joystick
          baseRadius={60}
          controllerRadius={30}
          directionCount={DirectionCount.Five}
          onChange={handleChange}
          onActiveChange={handleActiveChange}
          autoReset={true}
          throttle={50}
        />
      </GhostArea>
    </div>
  );
};

export default TouchControls;